import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronRight, Copy, AlertTriangle, CheckCircle, Clock, XCircle, Shield, AlertCircle } from 'lucide-react';

const ApiReference = () => {
  const [expandedEndpoint, setExpandedEndpoint] = useState<string | null>(null);
  const [copiedCode, setCopiedCode] = useState<string | null>(null);
  const [hmacTab, setHmacTab] = useState<'js' | 'python' | 'curl'>('js');
  const [hmacExpanded, setHmacExpanded] = useState(false);

  const copyToClipboard = async (code: string, id: string) => {
    await navigator.clipboard.writeText(code);
    setCopiedCode(id);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const endpoints = [
    {
      id: 'market-data',
      method: 'GET',
      path: '/functions/v1/market-data/{market_id}',
      title: 'Get Market Data',
      description: 'Retrieve detailed information about a specific market including options, odds, and trading volumes. Use "market-sample" as market_id to get the first available market matching your API key environment (test or production).',
      authentication: 'API Key only (no HMAC signing required)',
      parameters: [
        { name: 'market_id', type: 'string', required: true, description: 'Unique identifier for the market, or "market-sample" to get the first available market' }
      ],
      response: {
        success: `{
  "success": true,
  "data": {
    "market_id": "market-123",
    "title": "Will Bitcoin reach $100k by end of 2024?",
    "description": "Market description...",
    "category": "Crypto",
    "status": "active",
    "total_volume": 50000,
    "total_bets": 1250,
    "unique_traders": 89,
    "options": [
      {
        "id": 456,
        "name": "Yes",
        "probability": 0.65,
        "odds": 1.54,
        "volume": 32500,
        "bet_count": 890,
        "trading_end_time": null
      }
    ]
  }
}`,
        error: `{
  "success": false,
  "error": "Market not found",
  "error_code": "MARKET_NOT_FOUND"
}`
      }
    },
    {
      id: 'market-data-article',
      method: 'GET',
      path: '/functions/v1/market-data/article/{article_id}',
      title: 'Get Markets by Article',
      description: 'Retrieve all markets associated with a specific article or content piece.',
      authentication: 'API Key only (no HMAC signing required)',
      parameters: [
        { name: 'article_id', type: 'string', required: true, description: 'Unique identifier for the article' }
      ],
      response: {
        success: `{
  "success": true,
  "data": {
    "markets": [
      {
        "market_id": "market-123",
        "title": "Market Title",
        "options": [...],
        "total_volume": 50000
      }
    ]
  }
}`,
        error: `{
  "success": false,
  "error": "No markets found for this article",
  "error_code": "MARKET_NOT_FOUND"
}`
      }
    },
    {
      id: 'place-bet',
      method: 'POST',
      path: '/functions/v1/place-bet',
      title: 'Place Trade',
      description: 'Trade a market option with automatic odds calculation and wallet management.',
      authentication: 'API Key + HMAC Signature + User Token required',
      parameters: [
        { name: 'option_id', type: 'number', required: true, description: 'ID of the market option to trade on' },
        { name: 'amount', type: 'number', required: true, description: 'Trade amount in ZAR (between 0.01 and 100000)' },
        { name: 'user_token', type: 'string', required: true, description: 'Wayja session token identifying the user (obtained from OAuth flow)' },
        { name: 'market_id', type: 'string', required: false, description: 'Market ID (optional, can be inferred from option_id)' }
      ],
      response: {
        success: `{
  "success": true,
  "data": {
    "bet_id": "bet-789",
    "market_id": "market-123",
    "option_id": 456,
    "amount": 100,
    "odds": 1.54,
    "potential_win": 154,
    "created_at": "2024-01-15T10:30:00Z",
    "status": "active"
  }
}`,
        error: `{
  "success": false,
  "error": "Insufficient wallet balance",
  "error_code": "INSUFFICIENT_BALANCE"
}`
      }
    },
    {
      id: 'wallet-balance',
      method: 'GET',
      path: '/functions/v1/wallet-balance',
      title: 'Get Wallet Balance',
      description: 'Retrieve the current wallet balance for a user.',
      authentication: 'API Key + User Token required (no HMAC signing - read-only endpoint)',
      parameters: [
        { name: 'user_token', type: 'string', required: true, description: 'Wayja session token (query parameter or X-User-Token header)' }
      ],
      response: {
        success: `{
  "success": true,
  "data": {
    "user_id": "user-123",
    "balance": 1500.50,
    "currency": "ZAR",
    "last_updated": "2024-01-15T10:30:00Z"
  }
}`,
        error: `{
  "success": false,
  "error": "User not authenticated",
  "error_code": "AUTHENTICATION_REQUIRED"
}`
      }
    },
    {
      id: 'create-market',
      method: 'POST',
      path: '/functions/v1/external-create-market',
      title: 'Create Market',
      description: 'Create a new prediction market. Requires approved developer account. Markets can be linked to articles for widget integration.',
      authentication: 'API Key + HMAC Signature required',
      parameters: [
        { name: 'article_id', type: 'string', required: true, description: 'Unique identifier for the article this market is associated with' },
        { name: 'title', type: 'string', required: true, description: 'Market title (max 200 characters)' },
        { name: 'description', type: 'string', required: true, description: 'Market description' },
        { name: 'category', type: 'string', required: true, description: 'Market category (Crypto, Politics, Sports, Entertainment, Science, Economics, Gaming, Technology, Social Media, Legal, Climate, Health, Business, AI, Other)' },
        { name: 'market_type', type: 'string', required: true, description: 'Market type: "fixed" (single trading end time) or "knockout" (per-option trading times)' },
        { 
          name: 'options', 
          type: 'array', 
          required: true, 
          description: 'Array of market options (minimum 2, maximum 20). Each option is an object with "name" (required) and optionally "trading_end_time" (for knockout markets only). See examples below.' 
        },
        { name: 'resolution_time', type: 'string', required: true, description: 'ISO 8601 timestamp when market resolves' },
        { name: 'trading_end_time', type: 'string', required: false, description: 'ISO 8601 timestamp when trading ends (for fixed markets)' },
        { name: 'is_private', type: 'boolean', required: false, description: 'Whether market is private (default: false)' },
        { name: 'image_url', type: 'string', required: false, description: 'URL to market image' }
      ],
      response: {
        success: `{
  "success": true,
  "market_id": "market-123",
  "market_url": "https://wayja.me/markets/market-123",
  "admin_url": "https://wayja.me/admin/market/market-123",
  "widget_code": "<script>...</script>",
  "embed_url": "https://wayja.me/embed/market-123",
  "status": "active",
  "estimated_go_live": "2024-01-15T10:30:00Z",
  "commission_rate": 0.15,
  "tracking_id": "company-name_article-id",
  "partner_name": "Company Name"
}`,
        error: `{
  "success": false,
  "error": "Developer account is pending. Account must be approved to create markets.",
  "error_code": "DEVELOPER_ACCOUNT_NOT_APPROVED"
}`
      }
    },
    {
      id: 'wallet-topup',
      method: 'POST',
      path: '/functions/v1/wallet-topup',
      title: 'Top Up Wallet',
      badge: 'Coming Soon',
      description: 'Add funds to a user wallet using Peach Payments integration. This endpoint will be available once Peach Sandbox (test) and Peach Production (live) integrations are complete.',
      authentication: 'API Key + HMAC Signature + User Token required',
      parameters: [
        { name: 'user_token', type: 'string', required: true, description: 'Wayja session token identifying the user' },
        { name: 'amount', type: 'number', required: true, description: 'Top-up amount in ZAR (minimum R10)' },
        { name: 'card_number', type: 'string', required: true, description: 'Card number (16 digits)' },
        { name: 'card_holder', type: 'string', required: true, description: 'Cardholder name' },
        { name: 'card_expiry_month', type: 'string', required: true, description: 'Card expiry month (MM)' },
        { name: 'card_expiry_year', type: 'string', required: true, description: 'Card expiry year (YYYY)' },
        { name: 'card_cvv', type: 'string', required: true, description: 'Card CVV (3-4 digits)' },
        { name: 'return_url', type: 'string', required: false, description: 'URL to redirect after payment (optional)' }
      ],
      response: {
        success: `{
  "success": true,
  "data": {
    "transaction_id": "txn-123",
    "payment_id": "payment-456",
    "amount": 100,
    "requires_redirect": true,
    "redirect": {
      "url": "https://payment.redirect.url",
      "method": "POST"
    }
  }
}`,
        error: `{
  "success": false,
  "error": "Invalid card details",
  "error_code": "INVALID_CARD_DETAILS"
}`
      }
    },
  ];

  const toggleEndpoint = (endpointId: string) => {
    setExpandedEndpoint(expandedEndpoint === endpointId ? null : endpointId);
  };

  return (
    <div className="min-h-screen bg-wayja-cream">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-wayja-black">API Reference</h1>
              <p className="text-wayja-dark-slate mt-2">Complete documentation for Wayja's prediction market APIs</p>
            </div>
            <Link
              to="/developers"
              className="text-wayja-teal hover:text-wayja-dark-teal font-medium"
            >
              ← Back to Developer Portal
            </Link>
          </div>
        </div>
      </div>

      {/* API Info */}
      <div className="bg-blue-50 border-b border-blue-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-start space-x-3">
            <AlertTriangle className="w-5 h-5 text-blue-600 mt-0.5" />
            <div>
              <h3 className="text-sm font-medium text-blue-800">Base URL</h3>
              <p className="text-sm text-blue-700 mt-1">
                <code className="bg-blue-100 px-2 py-1 rounded">https://uyanvkwkikftzpanolmw.supabase.co</code>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Authentication */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <h2 className="text-2xl font-bold text-wayja-black mb-6">Authentication</h2>
          
          {/* API Key and HMAC Overview */}
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-gray-50 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-wayja-black mb-3">API Key Authentication</h3>
              <p className="text-wayja-dark-slate mb-4 text-sm">
                Include your API key in the X-API-Key header for all requests.
              </p>
              <div className="bg-gray-900 text-green-400 p-4 rounded-lg text-sm font-mono">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-gray-400">Header</span>
                  <button
                    onClick={() => copyToClipboard('X-API-Key: YOUR_API_KEY', 'api-key-header')}
                    className="text-gray-400 hover:text-white"
                  >
                    {copiedCode === 'api-key-header' ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </button>
                </div>
                <pre>X-API-Key: YOUR_API_KEY</pre>
              </div>
            </div>
            
            <div className="bg-gray-50 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-wayja-black mb-3">
                HMAC Request Signing 
                <span className="text-red-600 text-sm font-normal ml-2">(REQUIRED for state-modifying endpoints)</span>
              </h3>
              <p className="text-wayja-dark-slate mb-4 text-sm">
                State-modifying endpoints (place-bet, wallet-topup, etc.) must be signed with HMAC-SHA256 using your signing secret. This ensures request integrity and prevents replay attacks.
              </p>
              <div className="bg-gray-900 text-green-400 p-4 rounded-lg text-sm font-mono mb-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-gray-400">Required Headers</span>
                  <button
                    onClick={() => copyToClipboard('X-Request-Timestamp: {timestamp}\nX-Request-Nonce: {uuid}\nX-Request-Signature: {hmac_signature}', 'hmac-headers')}
                    className="text-gray-400 hover:text-white"
                  >
                    {copiedCode === 'hmac-headers' ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </button>
                </div>
                <pre>{`X-Request-Timestamp: {unix_timestamp_ms}
X-Request-Nonce: {unique_uuid}
X-Request-Signature: {hmac_sha256_signature}`}</pre>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-xs text-blue-800">
                  <strong>Signature Format:</strong> Create message as <code className="bg-blue-100 px-1 rounded">method\npath\ntimestamp\nnonce\nbody</code>, then sign with HMAC-SHA256 using your signing secret.
                </p>
              </div>
            </div>
          </div>

          {/* Collapsible HMAC Signature Examples */}
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <button
              onClick={() => setHmacExpanded(!hmacExpanded)}
              className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors"
            >
              <div className="flex items-center gap-3">
                {hmacExpanded ? <ChevronDown className="w-5 h-5 text-gray-500" /> : <ChevronRight className="w-5 h-5 text-gray-500" />}
                <span className="font-semibold text-wayja-black">HMAC Signature Generation - Code Examples</span>
                <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">JavaScript • Python • cURL</span>
              </div>
              <span className="text-sm text-gray-500">{hmacExpanded ? 'Click to collapse' : 'Click to expand'}</span>
            </button>
            
            {hmacExpanded && (
              <div className="p-4 border-t border-gray-200">
                {/* Language Tabs */}
                <div className="flex gap-1 mb-4 border-b border-gray-200">
                  <button
                    onClick={() => setHmacTab('js')}
                    className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
                      hmacTab === 'js' 
                        ? 'bg-gray-900 text-green-400 border-b-2 border-green-400' 
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                    }`}
                  >
                    <span className="flex items-center gap-2">
                      <span className="bg-blue-100 text-blue-800 px-1.5 py-0.5 rounded text-xs font-mono">JS</span>
                      JavaScript
                    </span>
                  </button>
                  <button
                    onClick={() => setHmacTab('python')}
                    className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
                      hmacTab === 'python' 
                        ? 'bg-gray-900 text-green-400 border-b-2 border-green-400' 
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                    }`}
                  >
                    <span className="flex items-center gap-2">
                      <span className="bg-yellow-100 text-yellow-800 px-1.5 py-0.5 rounded text-xs font-mono">PY</span>
                      Python
                    </span>
                  </button>
                  <button
                    onClick={() => setHmacTab('curl')}
                    className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
                      hmacTab === 'curl' 
                        ? 'bg-gray-900 text-green-400 border-b-2 border-green-400' 
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                    }`}
                  >
                    <span className="flex items-center gap-2">
                      <span className="bg-green-100 text-green-800 px-1.5 py-0.5 rounded text-xs font-mono">SH</span>
                      cURL
                    </span>
                  </button>
                </div>

                {/* JavaScript Tab Content */}
                {hmacTab === 'js' && (
                  <div className="bg-gray-900 text-green-400 p-4 rounded-lg text-sm font-mono overflow-x-auto">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-gray-400">JavaScript Example</span>
                      <button
                        onClick={() => copyToClipboard(`const crypto = require('crypto');

function generateHMACSignature(method, path, timestamp, nonce, body, signingSecret) {
  const message = \`\${method}\\n\${path}\\n\${timestamp}\\n\${nonce}\\n\${body}\`;
  const signature = crypto.createHmac('sha256', signingSecret)
    .update(message).digest('base64')
    .replace(/\\+/g, '-').replace(/\\//g, '_').replace(/=/g, '');
  return signature;
}

// Usage
const timestamp = Date.now().toString();
const nonce = crypto.randomUUID();
const body = JSON.stringify({ option_id: 456, amount: 100, user_token: 'token' });
// Note: Use /place-bet (not /functions/v1/place-bet) for HMAC signing
const sig = generateHMACSignature('POST', '/place-bet', timestamp, nonce, body, 'SECRET');`, 'hmac-js')}
                        className="text-gray-400 hover:text-white"
                      >
                        {copiedCode === 'hmac-js' ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                      </button>
                    </div>
                    <pre className="whitespace-pre-wrap">{`const crypto = require('crypto');

function generateHMACSignature(method, path, timestamp, nonce, body, signingSecret) {
  // Message format: method\\npath\\ntimestamp\\nnonce\\nbody
  const message = \`\${method}\\n\${path}\\n\${timestamp}\\n\${nonce}\\n\${body}\`;
  
  const signature = crypto.createHmac('sha256', signingSecret)
    .update(message)
    .digest('base64')
    .replace(/\\+/g, '-')  // URL-safe
    .replace(/\\//g, '_')
    .replace(/=/g, '');
  
  return signature;
}

// Example usage
const timestamp = Date.now().toString();
const nonce = crypto.randomUUID();
const body = JSON.stringify({ option_id: 456, amount: 100, user_token: 'wayja_token' });

// Note: Use /place-bet (not /functions/v1/place-bet) for HMAC signing
// Supabase Edge Functions strip the /functions/v1 prefix from the pathname
const signature = generateHMACSignature(
  'POST', '/place-bet', timestamp, nonce, body, 'YOUR_SIGNING_SECRET'
);

// Headers to include: X-Request-Timestamp, X-Request-Nonce, X-Request-Signature`}</pre>
                  </div>
                )}

                {/* Python Tab Content */}
                {hmacTab === 'python' && (
                  <div className="bg-gray-900 text-green-400 p-4 rounded-lg text-sm font-mono overflow-x-auto">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-gray-400">Python Example</span>
                      <button
                        onClick={() => copyToClipboard(`import hmac, hashlib, base64, json, uuid, time

def generate_hmac_signature(method, path, timestamp, nonce, body, signing_secret):
    message = f"{method}\\n{path}\\n{timestamp}\\n{nonce}\\n{body}"
    sig = hmac.new(signing_secret.encode(), message.encode(), hashlib.sha256).digest()
    return base64.b64encode(sig).decode().replace('+', '-').replace('/', '_').rstrip('=')

# Usage
timestamp = str(int(time.time() * 1000))
nonce = str(uuid.uuid4())
body = json.dumps({'option_id': 456, 'amount': 100, 'user_token': 'token'})
# Note: Use /place-bet (not /functions/v1/place-bet) for HMAC signing
sig = generate_hmac_signature('POST', '/place-bet', timestamp, nonce, body, 'SECRET')`, 'hmac-python')}
                        className="text-gray-400 hover:text-white"
                      >
                        {copiedCode === 'hmac-python' ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                      </button>
                    </div>
                    <pre className="whitespace-pre-wrap">{`import hmac, hashlib, base64, json, uuid, time

def generate_hmac_signature(method, path, timestamp, nonce, body, signing_secret):
    # Message format: method\\npath\\ntimestamp\\nnonce\\nbody
    message = f"{method}\\n{path}\\n{timestamp}\\n{nonce}\\n{body}"
    
    signature_bytes = hmac.new(
        signing_secret.encode('utf-8'),
        message.encode('utf-8'),
        hashlib.sha256
    ).digest()
    
    # Base64 encode and make URL-safe
    signature = base64.b64encode(signature_bytes).decode('utf-8')
    return signature.replace('+', '-').replace('/', '_').rstrip('=')

# Example usage
timestamp = str(int(time.time() * 1000))  # Unix ms
nonce = str(uuid.uuid4())
body = json.dumps({'option_id': 456, 'amount': 100, 'user_token': 'wayja_token'})

# Note: Use /place-bet (not /functions/v1/place-bet) for HMAC signing
# Supabase Edge Functions strip the /functions/v1 prefix from the pathname
signature = generate_hmac_signature(
    'POST', '/place-bet', timestamp, nonce, body, 'YOUR_SIGNING_SECRET'
)

# Headers: X-Request-Timestamp, X-Request-Nonce, X-Request-Signature`}</pre>
                  </div>
                )}

                {/* cURL Tab Content */}
                {hmacTab === 'curl' && (
                  <div className="bg-gray-900 text-green-400 p-4 rounded-lg text-sm font-mono overflow-x-auto">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-gray-400">cURL / Bash Example</span>
                      <button
                        onClick={() => copyToClipboard(`#!/bin/bash
API_KEY="YOUR_API_KEY"
SIGNING_SECRET="YOUR_SIGNING_SECRET"

METHOD="POST"
# Note: Use /place-bet (not /functions/v1/place-bet) for HMAC signing
PATH_FOR_SIGNING="/place-bet"
FULL_PATH="/functions/v1/place-bet"
TIMESTAMP=$(date +%s000)
NONCE=$(uuidgen)
BODY='{"option_id": 456, "amount": 100, "user_token": "wayja_token"}'

# Create message: method\\npath\\ntimestamp\\nnonce\\nbody
MESSAGE=$(printf "%s\\n%s\\n%s\\n%s\\n%s" "$METHOD" "$PATH_FOR_SIGNING" "$TIMESTAMP" "$NONCE" "$BODY")

# Generate URL-safe HMAC-SHA256 signature
SIGNATURE=$(echo -n "$MESSAGE" | openssl dgst -sha256 -hmac "$SIGNING_SECRET" -binary | base64 | tr '+/' '-_' | tr -d '=')

curl -X "$METHOD" "https://uyanvkwkikftzpanolmw.supabase.co$FULL_PATH" \\
  -H "X-API-Key: $API_KEY" \\
  -H "Content-Type: application/json" \\
  -H "X-Request-Timestamp: $TIMESTAMP" \\
  -H "X-Request-Nonce: $NONCE" \\
  -H "X-Request-Signature: $SIGNATURE" \\
  -d "$BODY"`, 'hmac-curl')}
                        className="text-gray-400 hover:text-white"
                      >
                        {copiedCode === 'hmac-curl' ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                      </button>
                    </div>
                    <pre className="whitespace-pre-wrap">{`#!/bin/bash
API_KEY="YOUR_API_KEY"
SIGNING_SECRET="YOUR_SIGNING_SECRET"

METHOD="POST"
# Note: Use /place-bet (not /functions/v1/place-bet) for HMAC signing
# Supabase Edge Functions strip the /functions/v1 prefix from the pathname
PATH_FOR_SIGNING="/place-bet"
FULL_PATH="/functions/v1/place-bet"
TIMESTAMP=$(date +%s000)  # Unix timestamp in milliseconds
NONCE=$(uuidgen)
BODY='{"option_id": 456, "amount": 100, "user_token": "wayja_token"}'

# Create message: method\\npath\\ntimestamp\\nnonce\\nbody
MESSAGE=$(printf "%s\\n%s\\n%s\\n%s\\n%s" "$METHOD" "$PATH_FOR_SIGNING" "$TIMESTAMP" "$NONCE" "$BODY")

# Generate URL-safe HMAC-SHA256 signature
SIGNATURE=$(echo -n "$MESSAGE" | \\
  openssl dgst -sha256 -hmac "$SIGNING_SECRET" -binary | \\
  base64 | tr '+/' '-_' | tr -d '=')

curl -X "$METHOD" "https://uyanvkwkikftzpanolmw.supabase.co$FULL_PATH" \\
  -H "X-API-Key: $API_KEY" \\
  -H "Content-Type: application/json" \\
  -H "X-Request-Timestamp: $TIMESTAMP" \\
  -H "X-Request-Nonce: $NONCE" \\
  -H "X-Request-Signature: $SIGNATURE" \\
  -d "$BODY"`}</pre>
                  </div>
                )}

                {/* Compact Important Notes */}
                <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-yellow-600 mt-0.5 flex-shrink-0" />
                    <div className="text-xs text-yellow-800">
                      <strong>Key Points:</strong> Message format is <code className="bg-yellow-100 px-1 rounded">method\npath\ntimestamp\nnonce\nbody</code> • 
                      Timestamp in <strong>milliseconds</strong> • 
                      Unique UUID nonce per request • 
                      URL-safe Base64: <code className="bg-yellow-100 px-1 rounded">+→- /→_ remove =</code>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* User Authentication - Full Width */}
          <div className="bg-gray-50 rounded-lg p-6 mt-6">
            <h3 className="text-xl font-semibold text-wayja-black mb-4">User Authentication</h3>
            <p className="text-wayja-dark-slate mb-4 text-sm">
              For user-specific operations (place-bet, wallet-balance, wallet-topup, etc.), include a Wayja session token identifying the user. <strong>There is no "sign in" API endpoint</strong> - users must authenticate directly with Wayja via OAuth 2.0 redirect flow to obtain this token.
            </p>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <div className="bg-gray-900 text-green-400 p-4 rounded-lg text-sm font-mono">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-gray-400">Request Body</span>
                    <button
                      onClick={() => copyToClipboard('"user_token": "eyJhbGciOiJIUzI1NiIs..."', 'user-token')}
                      className="text-gray-400 hover:text-white"
                    >
                      {copiedCode === 'user-token' ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    </button>
                  </div>
                  <pre>"user_token": "eyJhbGciOiJIUzI1NiIs..."</pre>
                </div>
              </div>
              
              <div>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-800 mb-2 font-semibold">
                    How to get user_token:
                  </p>
                  <ol className="text-sm text-blue-800 list-decimal list-inside space-y-1">
                    <li>Users must log in directly to Wayja (via <code className="bg-blue-100 px-1 rounded">wayja.me</code> or OAuth 2.0 redirect flow).</li>
                    <li>After successful authentication, Wayja issues a signed session token (<code className="bg-blue-100 px-1 rounded">user_token</code>).</li>
                    <li>Use this token in the request body for endpoints like <code className="bg-blue-100 px-1 rounded">place-bet</code> and <code className="bg-blue-100 px-1 rounded">wallet-balance</code>.</li>
                    <li>For external platforms/widgets, implement OAuth 2.0 redirect flow to Wayja login page.</li>
                  </ol>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Rate Limits */}
      <div className="bg-yellow-50 border-b border-yellow-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-start space-x-3">
            <Clock className="w-5 h-5 text-yellow-600 mt-0.5" />
            <div>
              <h3 className="text-sm font-medium text-yellow-800">Rate Limits</h3>
              <p className="text-sm text-yellow-700 mt-1">
                1000 requests per minute per API key. Contact support for higher limits.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Endpoints */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Endpoints</h2>

        <div className="space-y-4">
          {endpoints.map((endpoint) => (
            <div key={endpoint.id} className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              {/* Endpoint Header */}
              <div
                className="p-6 cursor-pointer hover:bg-gray-50"
                onClick={() => toggleEndpoint(endpoint.id)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                      endpoint.method === 'GET' ? 'bg-green-100 text-green-800' :
                      endpoint.method === 'POST' ? 'bg-blue-100 text-blue-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {endpoint.method}
                    </span>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="text-lg font-semibold text-gray-900">{endpoint.title}</h3>
                        {(endpoint as any).badge && (
                          <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs font-medium rounded-full">
                            {(endpoint as any).badge}
                          </span>
                        )}
                      </div>
                      <code className="text-sm text-gray-600 font-mono">{endpoint.path}</code>
                    </div>
                  </div>
                  {expandedEndpoint === endpoint.id ?
                    <ChevronDown className="w-5 h-5 text-gray-400" /> :
                    <ChevronRight className="w-5 h-5 text-gray-400" />
                  }
                </div>
                <p className="text-gray-600 mt-2">{endpoint.description}</p>
                <div className="flex items-center mt-2 text-sm text-gray-500">
                  <Shield className="w-4 h-4 mr-1" />
                  {endpoint.authentication}
                </div>
              </div>

              {/* Expanded Content */}
              {expandedEndpoint === endpoint.id && (
                <div className="border-t border-gray-200 p-6 space-y-6">
                  {/* Coming Soon Notice for wallet-topup */}
                  {endpoint.id === 'wallet-topup' && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                      <div className="flex items-start">
                        <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5 mr-3 flex-shrink-0" />
                        <div>
                          <h4 className="font-medium text-yellow-800 mb-1">🚧 Integration In Progress</h4>
                          <p className="text-yellow-700 text-sm mb-2">
                            The wallet top-up functionality is currently being integrated with Peach Payments:
                          </p>
                          <ul className="text-yellow-700 text-sm list-disc list-inside space-y-1 ml-2">
                            <li><strong>Test Environment:</strong> Peach Sandbox integration</li>
                            <li><strong>Production Environment:</strong> Peach Production integration</li>
                          </ul>
                          <p className="text-yellow-700 text-sm mt-2">
                            This endpoint will be available soon. Check back later or contact support for updates.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Options Array Details for create-market */}
                  {endpoint.id === 'create-market' && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <h4 className="text-lg font-semibold text-gray-900 mb-3">Options Array Structure</h4>
                      <p className="text-gray-700 text-sm mb-4">
                        The <code className="bg-blue-100 px-1 rounded">options</code> array contains 2-20 option objects. 
                        Each option object structure depends on the <code className="bg-blue-100 px-1 rounded">market_type</code>:
                      </p>
                      
                      <div className="space-y-4">
                        <div>
                          <h5 className="font-semibold text-gray-900 mb-2">For Fixed Markets:</h5>
                          <p className="text-gray-700 text-sm mb-2">
                            All options share the same trading end time (specified at market level). 
                            Each option only requires a <code className="bg-blue-100 px-1 rounded">name</code> field.
                          </p>
                          <div className="bg-gray-900 text-green-400 p-3 rounded text-xs font-mono overflow-x-auto">
                            <pre>{`"options": [
  { "name": "Yes" },
  { "name": "No" },
  { "name": "Maybe" }
]`}</pre>
                          </div>
                        </div>

                        <div>
                          <h5 className="font-semibold text-gray-900 mb-2">For Knockout Markets:</h5>
                          <p className="text-gray-700 text-sm mb-2">
                            Each option can have its own trading end time. 
                            Options require a <code className="bg-blue-100 px-1 rounded">name</code> field, 
                            and optionally <code className="bg-blue-100 px-1 rounded">trading_end_time</code> (ISO 8601 format).
                            If <code className="bg-blue-100 px-1 rounded">trading_end_time</code> is not provided, 
                            the option will use the market's resolution time.
                          </p>
                          <div className="bg-gray-900 text-green-400 p-3 rounded text-xs font-mono overflow-x-auto">
                            <pre>{`"options": [
  { 
    "name": "Team A",
    "trading_end_time": "2024-12-31T10:00:00Z"
  },
  { 
    "name": "Team B",
    "trading_end_time": "2024-12-31T11:00:00Z"
  },
  { 
    "name": "Team C"
    // No trading_end_time - uses market resolution_time
  }
]`}</pre>
                          </div>
                        </div>

                        <div className="bg-yellow-50 border border-yellow-200 rounded p-3">
                          <p className="text-yellow-800 text-xs font-medium mb-1">⚠️ Important Notes:</p>
                          <ul className="text-yellow-700 text-xs list-disc list-inside space-y-1">
                            <li>Minimum 2 options, maximum 20 options</li>
                            <li>Option names cannot be empty</li>
                            <li>For fixed markets, <code className="bg-yellow-100 px-1 rounded">trading_end_time</code> in options is ignored</li>
                            <li>For knockout markets, <code className="bg-yellow-100 px-1 rounded">trading_end_time</code> must be before the market's <code className="bg-yellow-100 px-1 rounded">resolution_time</code></li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Parameters */}
                  {endpoint.parameters.length > 0 && (
                    <div>
                      <h4 className="text-lg font-semibold text-gray-900 mb-3">Parameters</h4>
                      <div className="overflow-x-auto">
                        <table className="min-w-full">
                          <thead>
                            <tr className="border-b border-gray-200">
                              <th className="text-left py-2 px-4 font-medium text-gray-700">Name</th>
                              <th className="text-left py-2 px-4 font-medium text-gray-700">Type</th>
                              <th className="text-left py-2 px-4 font-medium text-gray-700">Required</th>
                              <th className="text-left py-2 px-4 font-medium text-gray-700">Description</th>
                            </tr>
                          </thead>
                          <tbody>
                            {endpoint.parameters.map((param, index) => (
                              <tr key={index} className="border-b border-gray-100">
                                <td className="py-2 px-4 font-mono text-sm">{param.name}</td>
                                <td className="py-2 px-4 text-sm text-gray-600">{param.type}</td>
                                <td className="py-2 px-4">
                                  {param.required ?
                                    <span className="text-red-600 font-medium">Yes</span> :
                                    <span className="text-gray-600">No</span>
                                  }
                                </td>
                                <td className="py-2 px-4 text-sm text-gray-600">{param.description}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Example Request */}
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-3">Example Request</h4>
                    {['place-bet', 'create-market', 'wallet-topup'].includes(endpoint.id) ? (
                      <div className="bg-gray-900 text-green-400 p-4 rounded-lg text-sm font-mono overflow-x-auto">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-gray-400">cURL (with HMAC signing)</span>
                          <button
                            onClick={() => {
                              const body = endpoint.id === 'place-bet' 
                                ? '{"option_id": 456, "amount": 100, "user_token": "wayja_token"}'
                                : endpoint.id === 'create-market'
                                ? '{"article_id": "article-123", "title": "Market Title", "description": "Description", "category": "Crypto", "market_type": "fixed", "options": [{"name": "Yes"}, {"name": "No"}], "resolution_time": "2024-12-31T23:59:59Z", "trading_end_time": "2024-12-31T20:00:00Z", "is_private": false}'
                                : '{"user_token": "wayja_token", "amount": 100, "card_number": "4111111111111111", "card_holder": "John Doe", "card_expiry_month": "12", "card_expiry_year": "2025", "card_cvv": "123"}';
                              const path = endpoint.path.replace('{article_id}', 'article-123').replace('{market_id}', 'market-123');
                              // Extract path for signing (remove /functions/v1 prefix)
                              const pathForSigning = path.replace('/functions/v1', '');
                              copyToClipboard(`TIMESTAMP=$(date +%s000)\nNONCE=$(uuidgen)\nBODY='${body}'\n# Note: Use ${pathForSigning} (not ${path}) for HMAC signing\nSIGNATURE=$(echo -n "${endpoint.method}\\n${pathForSigning}\\n$TIMESTAMP\\n$NONCE\\n$BODY" | openssl dgst -sha256 -hmac "YOUR_SIGNING_SECRET" -binary | base64 | tr '+/' '-_' | tr -d '=')\n\ncurl -X ${endpoint.method} https://uyanvkwkikftzpanolmw.supabase.co${path} \\\n  -H "X-API-Key: YOUR_API_KEY" \\\n  -H "Content-Type: application/json" \\\n  -H "X-Request-Timestamp: $TIMESTAMP" \\\n  -H "X-Request-Nonce: $NONCE" \\\n  -H "X-Request-Signature: $SIGNATURE" \\\n  -d "$BODY"`, `curl-${endpoint.id}`);
                            }}
                            className="text-gray-400 hover:text-white"
                          >
                            {copiedCode === `curl-${endpoint.id}` ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                          </button>
                        </div>
                        <pre className="whitespace-pre-wrap break-words">{endpoint.id === 'place-bet' ? `TIMESTAMP=$(date +%s000)
NONCE=$(uuidgen)
BODY='{"option_id": 456, "amount": 100, "user_token": "wayja_token"}'
# Note: Use /place-bet (not /functions/v1/place-bet) for HMAC signing
SIGNATURE=$(echo -n "POST\\n/place-bet\\n$TIMESTAMP\\n$NONCE\\n$BODY" | \\
  openssl dgst -sha256 -hmac "YOUR_SIGNING_SECRET" -binary | \\
  base64 | tr '+/' '-_' | tr -d '=')

curl -X POST https://uyanvkwkikftzpanolmw.supabase.co/functions/v1/place-bet \\
  -H "X-API-Key: YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -H "X-Request-Timestamp: $TIMESTAMP" \\
  -H "X-Request-Nonce: $NONCE" \\
  -H "X-Request-Signature: $SIGNATURE" \\
  -d "$BODY"` : endpoint.id === 'create-market' ? `TIMESTAMP=$(date +%s000)
NONCE=$(uuidgen)
BODY='{"article_id": "article-123", "title": "Market Title", "description": "Description", "category": "Crypto", "market_type": "fixed", "options": [{"name": "Yes"}, {"name": "No"}], "resolution_time": "2024-12-31T23:59:59Z", "trading_end_time": "2024-12-31T20:00:00Z", "is_private": false}'
# Note: Use /external-create-market (not /functions/v1/external-create-market) for HMAC signing
SIGNATURE=$(echo -n "POST\\n/external-create-market\\n$TIMESTAMP\\n$NONCE\\n$BODY" | \\
  openssl dgst -sha256 -hmac "YOUR_SIGNING_SECRET" -binary | \\
  base64 | tr '+/' '-_' | tr -d '=')

curl -X POST https://uyanvkwkikftzpanolmw.supabase.co/functions/v1/external-create-market \\
  -H "X-API-Key: YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -H "X-Request-Timestamp: $TIMESTAMP" \\
  -H "X-Request-Nonce: $NONCE" \\
  -H "X-Request-Signature: $SIGNATURE" \\
  -d "$BODY"` : `TIMESTAMP=$(date +%s000)
NONCE=$(uuidgen)
BODY='{"user_token": "wayja_token", "amount": 100, "card_number": "4111111111111111", "card_holder": "John Doe", "card_expiry_month": "12", "card_expiry_year": "2025", "card_cvv": "123"}'
# Note: Use /wallet-topup (not /functions/v1/wallet-topup) for HMAC signing
SIGNATURE=$(echo -n "POST\\n/wallet-topup\\n$TIMESTAMP\\n$NONCE\\n$BODY" | \\
  openssl dgst -sha256 -hmac "YOUR_SIGNING_SECRET" -binary | \\
  base64 | tr '+/' '-_' | tr -d '=')

curl -X POST https://uyanvkwkikftzpanolmw.supabase.co/functions/v1/wallet-topup \\
  -H "X-API-Key: YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -H "X-Request-Timestamp: $TIMESTAMP" \\
  -H "X-Request-Nonce: $NONCE" \\
  -H "X-Request-Signature: $SIGNATURE" \\
  -d "$BODY"`}</pre>
                      </div>
                    ) : endpoint.id === 'wallet-balance' ? (
                      <div className="bg-gray-900 text-green-400 p-4 rounded-lg text-sm font-mono">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-gray-400">cURL</span>
                          <button
                            onClick={() => copyToClipboard(`curl -H "X-API-Key: YOUR_API_KEY" \\\n  "https://uyanvkwkikftzpanolmw.supabase.co/functions/v1/wallet-balance?user_token=wayja_token"`, `curl-${endpoint.id}`)}
                            className="text-gray-400 hover:text-white"
                          >
                            {copiedCode === `curl-${endpoint.id}` ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                          </button>
                        </div>
                        <pre>{`curl -H "X-API-Key: YOUR_API_KEY" \\
  "https://uyanvkwkikftzpanolmw.supabase.co/functions/v1/wallet-balance?user_token=wayja_token"`}</pre>
                      </div>
                    ) : (
                      <div className="bg-gray-900 text-green-400 p-4 rounded-lg text-sm font-mono">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-gray-400">cURL</span>
                          <button
                            onClick={() => copyToClipboard(`curl -X ${endpoint.method} https://uyanvkwkikftzpanolmw.supabase.co${endpoint.path} \\\n  -H "X-API-Key: YOUR_API_KEY"`, `curl-${endpoint.id}`)}
                            className="text-gray-400 hover:text-white"
                          >
                            {copiedCode === `curl-${endpoint.id}` ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                          </button>
                        </div>
                        <pre>{`curl -X ${endpoint.method} https://uyanvkwkikftzpanolmw.supabase.co${endpoint.path} \\
  -H "X-API-Key: YOUR_API_KEY"`}</pre>
                      </div>
                    )}
                  </div>

                  {/* Response */}
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-3">Response</h4>

                    {/* Success Response */}
                    <div className="mb-4">
                      <div className="flex items-center mb-2">
                        <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
                        <span className="font-medium text-gray-900">Success Response (200)</span>
                      </div>
                      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-green-800">JSON</span>
                          <button
                            onClick={() => copyToClipboard(endpoint.response.success, `success-${endpoint.id}`)}
                            className="text-green-600 hover:text-green-700"
                          >
                            {copiedCode === `success-${endpoint.id}` ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                          </button>
                        </div>
                        <pre className="text-sm text-green-800 whitespace-pre-wrap font-mono">{endpoint.response.success}</pre>
                      </div>
                    </div>

                    {/* Error Response */}
                    <div>
                      <div className="flex items-center mb-2">
                        <XCircle className="w-5 h-5 text-red-600 mr-2" />
                        <span className="font-medium text-gray-900">Error Response (4xx)</span>
                      </div>
                      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-red-800">JSON</span>
                          <button
                            onClick={() => copyToClipboard(endpoint.response.error, `error-${endpoint.id}`)}
                            className="text-red-600 hover:text-red-700"
                          >
                            {copiedCode === `error-${endpoint.id}` ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                          </button>
                        </div>
                        <pre className="text-sm text-red-800 whitespace-pre-wrap font-mono">{endpoint.response.error}</pre>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Footer */}
      <div className="bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <h3 className="text-xl font-semibold mb-4">Need Help?</h3>
            <p className="text-gray-300 mb-6">
              Can't find what you're looking for? Our developer support team is here to help.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/developers/examples"
                className="bg-wayja-teal hover:bg-wayja-dark-teal text-white px-6 py-3 rounded-lg font-medium transition-colors"
              >
                View Code Examples
              </Link>
              <a
                href="mailto:support@wayja.me"
                className="border border-gray-600 hover:border-gray-500 text-gray-300 hover:text-white px-6 py-3 rounded-lg font-medium transition-colors"
              >
                Contact Support
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApiReference;
